import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import HomepageHeader from '../../components/homepage-header/homepage-header.component';
import PositionCard from '../../components/position-card/position-card.component';
import AtsInitializationDialogs
  from '../../../../components/dialogs/ats-initialization-dialogs/ats-initialization-dialogs.component';
import { ANALYTICS_EVENTS, EVENT_GROUP, PRODUCT_AREA } from '../../../../consts/analytics';
import { useGetOpenPositionsQuery } from '../../../../store/rtk-query/apis/position/hooks/get-open-positions.query-hook';
import CreatePositionCard from '../../components/create-position-card/create-position-card.component';
import { CREATE_POSITION_ANALYTICS_EVENTS } from '../../../create-position/consts/position-analytics.consts';
import { useGetMyAccountSelector } from '../../../../store/rtk-query/apis/account/selectors/get-account-by-id.selector';
import { useAnalyticsContext } from '../../../../contexts/analytics/useAnalyticsContext';
import CreatePosition from '../../../create-position/create-position.component';
import HomepageBodyHeader from '../../components/homepage-body-header/homepage-body-header.component';
import { useAppState } from '../../../../store/selectors';
import { PAGE_TITLES } from '../../../../consts';
import { LOGIN_STATE } from '../../../../store/slices/app-state/app-state.consts';
import { PAGE_NAMES } from '../../../../consts/pages';
import ClosePositionModal from '../../../../components/dialogs/close-position-modal/close-position-modal.component';
import InviteColleagueModal
  from '../../../../components/dialogs/invite-colleague-modal/invite-colleague-modal.component';
import { PositionResponse } from '../../../../store/rtk-query/apis/position/dto/response/position.response';
import { HEADER_CONTAINER_HEIGHT_PX } from '../../components/homepage-header/homepage-header.styled';
import { HEADER_HEIGHT } from '../../../../components/Common/headers/header/header.styled';
import { PositionState } from '../../../../enums/position-state.enum';
import { paymentQuery } from '../../../../store/rtk-query/apis/payment/payment.toolkit-api';
import { PricingPlanEnum } from '../../../../enums/user-plan.enum';
import { useGetPositionPriceSelector } from '../../../../store/rtk-query/apis/payment/selectors/get-position-price.selector';
import { usePositionLimitUpgradeModal } from '../../../../hooks/position-limit-upgrade-modal';
import EmailConnectionErrorNotification
  from '../../../../components/dialogs/email-connection-error-notification/email-connection-error-notification.component';
import {
  BodyContainer, BodyWrapper, Container, HeaderContainer, PositionsContainer,
} from './home.styled';

const Homepage = (): JSX.Element => {
  const { data: positions } = useGetOpenPositionsQuery(false, true);
  const { data: { plan: { maximumOpenPositions = 0, pricingPlan, positionsLimit = 0 } } } = useGetMyAccountSelector();
  const { analyticsTrackEvent, analyticsPageEvent } = useAnalyticsContext();
  const { loginState } = useAppState();
  const headerRef = useRef<HTMLDivElement>(null);

  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isCreatePositionDialogOpen, setIsCreatePositionDialogOpen] = useState(false);
  const [isAtsInitializationDialogOpen, setIsAtsInitializationDialogOpen] = useState(false);
  const [atsInitializationPositionId, setAtsInitializationPositionId]  = useState<string>('');
  const [closePositionId, setClosePositionId]  = useState<string>('');
  const [isClosePositionModalOpen, setIsClosePositionModalOpen] = useState<boolean>(false);
  const [isInviteUsersModalOpen, setIsInviteUsersModalOpen] = useState(false);
  const { data: { price: positionPrice } } = useGetPositionPriceSelector();
  const [getPositionPrice] = paymentQuery.useLazyGetPositionPriceQuery();
  const [isPositionLimitUpgradeDialogOpen, setIsPositionLimitUpgradeDialogOpen] = useState(false);

  const openPositionsAmount = useMemo(() => positions.filter((position) => position.state === PositionState.Open).length, [positions]);

  const UpgradeModal = usePositionLimitUpgradeModal({
    isOpen: isPositionLimitUpgradeDialogOpen,
    createPositionHandler: () => {
      setIsPositionLimitUpgradeDialogOpen(false);
      setIsCreatePositionDialogOpen(true);
    },
    closeHandler: () => setIsPositionLimitUpgradeDialogOpen(false),
  });

  useEffect(() => {
    document.title = PAGE_TITLES.HOME;

    if (loginState === LOGIN_STATE.LOGGED_IN) {
      analyticsPageEvent({
        eventName: ANALYTICS_EVENTS.HOME_PAGE_VIEWED,
        eventGroup: EVENT_GROUP.PAGE_VIEW,
        pageName: PAGE_NAMES.HOME_PAGE,
      });
    }

    if (pricingPlan === PricingPlanEnum.PRO && maximumOpenPositions < positionsLimit && !positionPrice) {
      getPositionPrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    setIsHeaderSticky(event.currentTarget?.scrollTop >= (HEADER_CONTAINER_HEIGHT_PX - HEADER_HEIGHT));
  };

  const onClickClosePosition = (positionId: string) => {
    setIsClosePositionModalOpen(true);
    setClosePositionId(positionId);
  };

  const onClickClosePositionDialog = () => {
    setIsClosePositionModalOpen(false);
    setClosePositionId('');
  };

  const onClickAtsInitialization = (positionId: string) => {
    setIsAtsInitializationDialogOpen(true);
    setAtsInitializationPositionId(positionId);
  };

  const onClickCloseAtsInitializationDialog = () => {
    setIsAtsInitializationDialogOpen(false);
    setAtsInitializationPositionId('');
  };

  const onCreatePositionButtonClick = () => {
    const eventData = {
      eventName: CREATE_POSITION_ANALYTICS_EVENTS.NEW_POSITION_CLICKED_FROM_HOME_PAGE,
      eventGroup: EVENT_GROUP.CLICK,
      pageName: PRODUCT_AREA.HOMEPAGE,
      positionQuota: maximumOpenPositions,
    };
    analyticsTrackEvent(eventData);

    if (openPositionsAmount >= maximumOpenPositions) {
      setIsPositionLimitUpgradeDialogOpen(true);
    } else {
      setIsCreatePositionDialogOpen(true);
    }
  };

  const onInviteUsersButtonClick = () => {
    setIsInviteUsersModalOpen(true);

    const payload = {
      eventName: ANALYTICS_EVENTS.CLICKED_ON_INVITE_TEAMMATE,
      eventGroup: EVENT_GROUP.CLICK,
    };
    analyticsTrackEvent(payload);
  };

  const renderPositions = (positionsArray: PositionResponse[]) => {
    return (
      positionsArray.map((position, index) => (
        <PositionCard
          key={ position.id }
          position={ position }
          closePositionHandler={ onClickClosePosition }
          atsInitializationHandler={ onClickAtsInitialization }
          positionIndex={ index }
        />
      ))
    );
  };

  return (
    <>
      <Container onScroll={ handleScroll } ref={ headerRef }>
        <HeaderContainer isHeaderSticky={ isHeaderSticky }>
          <HomepageHeader />
        </HeaderContainer>
        <BodyContainer>
          <BodyWrapper>
            <HomepageBodyHeader
              inviteUserButtonClickHandler={ onInviteUsersButtonClick }
              createPositionButtonClickHandler={ onCreatePositionButtonClick }
              upgradeButtonClickHandler={ () => setIsPositionLimitUpgradeDialogOpen(true) }
            />
            <PositionsContainer>
              { renderPositions(positions) }
              <CreatePositionCard clickHandler={ onCreatePositionButtonClick } />
            </PositionsContainer>
          </BodyWrapper>
        </BodyContainer>
      </Container>
      <CreatePosition
        isCreatePositionDialogOpen={ isCreatePositionDialogOpen }
        closeHandler={ () => setIsCreatePositionDialogOpen(false) }
      />
      { UpgradeModal }
      { atsInitializationPositionId && (
        <AtsInitializationDialogs
          positionId={ atsInitializationPositionId }
          isOpen={ isAtsInitializationDialogOpen }
          closeHandler={ () => onClickCloseAtsInitializationDialog() }
          productArea={ PRODUCT_AREA.HOMEPAGE }
        />
      ) }
      {
        isClosePositionModalOpen && (
          <ClosePositionModal
            positionId={ closePositionId }
            onClose={ onClickClosePositionDialog }
          />
        )
      }
      {
        isInviteUsersModalOpen && (
          <InviteColleagueModal
            isOpen={ isInviteUsersModalOpen }
            closeHandler={ () => setIsInviteUsersModalOpen(false) }
          />
        )
      }
      <EmailConnectionErrorNotification />
    </>
  );
};

export default Homepage;
